// LiveScannerFeed — extracted from App.js during the Trading Hub rebuild (M4).
// Pure relocation, no logic changes.
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, fmtN } from "../lib/appCommon";
import { openSSE } from "../lib/sse";

function LiveScannerFeed() {
  const [events, setEvents] = useState([]);
  const [latestScan, setLatestScan] = useState(null);
  const [status, setStatus] = useState(null);
  const [connected, setConnected] = useState(false);
  const [insightMap, setInsightMap] = useState({});   // ticker-strike-type → {text, verdict, loading}
  const [logInsights, setLogInsights] = useState({}); // ev.ts → {text, loading, open}

  useEffect(() => {
    // openSSE: silent-stream watchdog + reconnect. Before, onerror only set
    // connected=false — a stream that went quiet (or a dropped upstream) was
    // never reopened, so this feed could stay dark until a full page reload.
    const conn = openSSE(`${API}/stream/scanner`, {
      replay: (logs) => { if (Array.isArray(logs)) setEvents(prev => [...logs.slice(-30), ...prev].slice(0, 100)); },
      scan: (d) => setLatestScan(d),
      thinking: (log) => { if (log) setEvents(prev => [log, ...prev].slice(0, 100)); },
      status: (d) => setStatus(d),
    }, { onStatus: setConnected });
    const poller = setInterval(() => {
      axios.get(`${API}/autopilot/thinking?limit=30`).then(r => {
        const logs = Array.isArray(r.data) ? r.data : [];
        if (logs.length > 0) {
          setEvents(prev => {
            const existingTs = new Set(prev.map(e => e.ts));
            const newLogs = logs.filter(l => !existingTs.has(l.ts));
            return newLogs.length > 0 ? [...newLogs, ...prev].slice(0, 100) : prev;
          });
        }
      }).catch(() => {});
    }, 10000);
    return () => { conn.close(); clearInterval(poller); };
  }, []);

  // Fetch Omega Zi AI insight for each top scan result (up to 5)
  useEffect(() => {
    if (!latestScan?.results?.length) return;
    const top = (latestScan.results || []).slice(0, 5);
    top.forEach(r => {
      const key = `${r.ticker}-${r.strike}-${r.optionType}`;
      setInsightMap(prev => ({ ...prev, [key]: { text: null, verdict: null, loading: true } }));
      axios.post(`${API}/zi/chat`, {
        message: `Omega Zi AI — give a sharp 2-sentence trade insight for this scanner hit:
${r.ticker} $${r.strike} ${(r.optionType || "").toUpperCase()} | Zi Score: ${r.ziScore} (${r.ziClass?.replace("_"," ")}) | Omega: ${r.omegaVerdict?.replace("_"," ") || "?"} | PoP: ${r.mcPoP ? (r.mcPoP * 100).toFixed(0) : "?"}% | Mid: $${fmtN(r.mid, 2)}
Focus on: entry merit, key risk, and whether momentum supports this right now. End your response with exactly one word on its own line: BUY, HOLD, or AVOID.`,
        sessionId: `scanner-${key}-${Date.now()}`,
      }).then(res => {
        const raw = res.data?.response || "";
        const lines = raw.trim().split("\n");
        const lastWord = lines[lines.length - 1].trim().toUpperCase();
        const verdict = ["BUY","HOLD","AVOID"].includes(lastWord) ? lastWord : null;
        const text = verdict ? lines.slice(0, -1).join(" ").trim() : raw.trim();
        setInsightMap(prev => ({ ...prev, [key]: { text, verdict, loading: false } }));
      }).catch(() => {
        setInsightMap(prev => ({ ...prev, [key]: { text: null, verdict: null, loading: false } }));
      });
    });
  }, [latestScan?.scannedAt]);

  // Lazy-fetch insight for a thinking log event on demand
  const fetchLogInsight = (ev) => {
    const key = ev.ts;
    if (logInsights[key]?.text || logInsights[key]?.loading) {
      // toggle open/close
      setLogInsights(prev => ({ ...prev, [key]: { ...prev[key], open: !prev[key]?.open } }));
      return;
    }
    setLogInsights(prev => ({ ...prev, [key]: { text: null, loading: true, open: true } }));
    const prompt = `Omega Zi AI — explain this scanner decision in 2 sentences for a trader:
${ev.ticker ? `Symbol: ${ev.ticker} | ` : ""}Stage: ${ev.stage} | Message: ${ev.message}${ev.education ? ` | Context: ${ev.education}` : ""}
What does this mean for the trade and should the trader act on it?`;
    axios.post(`${API}/zi/chat`, { message: prompt, sessionId: `log-${key}` })
      .then(res => {
        setLogInsights(prev => ({ ...prev, [key]: { text: res.data?.response || "No insight available.", loading: false, open: true } }));
      }).catch(() => {
        setLogInsights(prev => ({ ...prev, [key]: { text: "Insight unavailable.", loading: false, open: true } }));
      });
  };

  const verdictStyle = (v) => ({
    BUY:   { bg: "rgba(74,222,128,0.15)",  color: "#4ade80",  border: "rgba(74,222,128,0.3)"  },
    HOLD:  { bg: "rgba(251,191,36,0.12)",  color: "#fbbf24",  border: "rgba(251,191,36,0.3)"  },
    AVOID: { bg: "rgba(248,113,113,0.12)", color: "#f87171",  border: "rgba(248,113,113,0.3)" },
  }[v] || { bg: "rgba(167,139,250,0.1)", color: "#a78bfa", border: "rgba(167,139,250,0.2)" });

  return (
    <div data-testid="live-scanner-feed">
      <div className="card" style={{ borderColor: connected ? "rgba(74,222,128,0.15)" : "rgba(248,113,113,0.15)" }}>
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="sh" style={{ margin: 0 }}>
            Live Scanner Feed
            <span className={`tag ${connected ? "t-bull" : "t-bear"}`} style={{ fontSize: 8, marginLeft: 6 }}>{connected ? "CONNECTED" : "DISCONNECTED"}</span>
          </div>
          {status && (
            <div style={{ fontSize: 10, color: "var(--muted)" }}>
              {status.tier?.toUpperCase()} | ${fmtN(status.equity, 2)} | Market: {status.marketOpen ? "OPEN" : "CLOSED"}
            </div>
          )}
        </div>

        {/* Latest Scan Summary — with per-result Omega Zi AI insights */}
        {latestScan && (
          <div style={{ padding: 12, borderRadius: 8, background: "rgba(56,189,248,0.04)", border: "1px solid rgba(56,189,248,0.08)", marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#38bdf8" }}>Latest Scan</span>
              <span style={{ fontSize: 9, color: "var(--muted)" }}>{latestScan.scannedAt ? new Date(latestScan.scannedAt).toLocaleTimeString() : ""}</span>
            </div>
            <div style={{ fontSize: 10, color: "var(--muted)", marginBottom: 8 }}>
              Found {latestScan.count || 0} opportunities | Tier: {latestScan.tier || "?"} | Autonomy: {latestScan.autonomy || "?"} | Drift: {latestScan.drift || "none"}
            </div>
            {(latestScan.results || []).map((r, i) => {
              const key = `${r.ticker}-${r.strike}-${r.optionType}`;
              const insight = insightMap[key];
              const vs = insight?.verdict ? verdictStyle(insight.verdict) : null;
              return (
                <div key={i} style={{ borderTop: i > 0 ? "1px solid var(--border)" : "none", paddingTop: i > 0 ? 10 : 0, marginTop: i > 0 ? 10 : 0 }}>
                  {/* Contract row */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10, flexWrap: "wrap" }}>
                    <span className={`tag ${r.ziClass === "ULTRA_GEM" ? "t-bull" : r.ziClass === "GEM" ? "t-blue" : r.ziClass === "NEUTRAL" ? "t-neu" : "t-bear"}`} style={{ fontSize: 8, minWidth: 55, textAlign: "center" }}>{r.ziClass?.replace("_", " ")}</span>
                    {r.omegaVerdict && <span className={`tag ${r.omegaVerdict === "ULTRA_GEM" ? "t-bull" : r.omegaVerdict === "GEM" ? "t-blue" : r.omegaVerdict === "FORBIDDEN" ? "t-bear" : "t-neu"}`} style={{ fontSize: 7 }}>Ω {r.omegaVerdict?.replace("_"," ")}</span>}
                    <span style={{ fontWeight: 700, color: "var(--text)", minWidth: 40 }}>{r.ticker}</span>
                    <span style={{ color: "var(--muted)" }}>${r.strike} {(r.optionType || "").toUpperCase()}</span>
                    <span style={{ color: "#a78bfa" }}>Zi {r.ziScore}</span>
                    <span style={{ color: "var(--muted)" }}>PoP {r.mcPoP ? (r.mcPoP * 100).toFixed(0) : "?"}%</span>
                    <span style={{ color: "var(--muted)" }}>Mid ${fmtN(r.mid, 2)}</span>
                    {insight?.verdict && (
                      <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: vs.bg, color: vs.color, border: `1px solid ${vs.border}`, marginLeft: "auto" }}>
                        ◆ {insight.verdict}
                      </span>
                    )}
                    {insight?.loading && <span style={{ fontSize: 8, color: "#a78bfa", marginLeft: "auto" }}>◆ Omega thinking...</span>}
                  </div>
                  {/* AI Insight */}
                  {insight?.text && (
                    <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 6, background: vs ? vs.bg : "rgba(167,139,250,0.06)", borderLeft: `3px solid ${vs ? vs.color : "#a78bfa"}`, fontSize: 10, color: "var(--text)", lineHeight: 1.5 }}>
                      <span style={{ fontSize: 8, fontWeight: 700, color: vs ? vs.color : "#a78bfa", marginRight: 6 }}>◆ OMEGA ZI AI</span>
                      {insight.text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Thinking Log Stream */}
        <div style={{ maxHeight: 500, overflow: "auto" }} data-testid="thinking-stream">
          {events.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: "var(--dim)", fontSize: 11 }}>
              Waiting for scanner activity... The Omega scanner runs every 5 minutes.
            </div>
          ) : events.map((ev, i) => {
            const isDecision = ev.message?.includes("DECISION LOCKED") || ev.message?.includes("EXECUTE");
            const isBlock = ev.message?.includes("FILTERED") || ev.message?.includes("BLOCKED") || ev.message?.includes("FAIL");
            const isKS = ev.message?.includes("KILL SWITCH") || ev.message?.includes("DRIFT");
            const isScore = ev.message?.includes("Zi Score");
            const canAsk = isDecision || isScore || (ev.ticker && ev.stage >= 7);
            const stageLabel = {0:"PRE",1:"SCAN",2:"LIQ",3:"MACRO",4:"TEA",5:"IV",6:"MC",7:"GREEKS",8:"ZI",9:"RISK",10:"LOCK"}[ev.stage] || `S${ev.stage}`;
            const li = logInsights[ev.ts];
            return (
              <div key={i} style={{ padding: "6px 8px", borderBottom: "1px solid var(--border)", fontSize: 10, background: isDecision ? "rgba(74,222,128,0.03)" : isBlock ? "rgba(248,113,113,0.02)" : isKS ? "rgba(248,113,113,0.04)" : "transparent" }}>
                <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                  <span style={{ color: "var(--dim)", minWidth: 55, fontSize: 9 }}>{ev.ts ? new Date(ev.ts).toLocaleTimeString() : ""}</span>
                  <span style={{
                    minWidth: 40, padding: "1px 4px", borderRadius: 3, fontSize: 8, fontWeight: 700, textAlign: "center",
                    background: isDecision ? "rgba(74,222,128,0.15)" : isBlock ? "rgba(248,113,113,0.12)" : isKS ? "rgba(248,113,113,0.2)" : isScore ? "rgba(167,139,250,0.12)" : "rgba(56,189,248,0.08)",
                    color: isDecision ? "#4ade80" : isBlock ? "#f87171" : isKS ? "#f87171" : isScore ? "#a78bfa" : "#38bdf8",
                  }}>{stageLabel}</span>
                  <span style={{ color: ev.ticker ? "var(--text)" : "var(--muted)", fontWeight: ev.ticker ? 600 : 400, minWidth: 36 }}>{ev.ticker || ""}</span>
                  <span style={{ color: isDecision ? "#4ade80" : isBlock ? "#f87171" : "var(--muted)", flex: 1 }}>{ev.message}</span>
                  {canAsk && (
                    <button onClick={() => fetchLogInsight(ev)} style={{ fontSize: 8, padding: "1px 6px", borderRadius: 3, border: "1px solid rgba(167,139,250,0.3)", background: li?.open ? "rgba(167,139,250,0.15)" : "transparent", color: "#a78bfa", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
                      {li?.loading ? "..." : li?.open ? "▲ Omega" : "◆ Omega"}
                    </button>
                  )}
                </div>
                {ev.education && (
                  <div style={{ marginLeft: 101, marginTop: 3, padding: "4px 8px", borderRadius: 4, background: "rgba(56,189,248,0.03)", borderLeft: "2px solid rgba(56,189,248,0.15)", fontSize: 9, color: "var(--dim)", lineHeight: 1.4 }}>
                    {ev.education}
                  </div>
                )}
                {li?.open && li?.text && (
                  <div style={{ marginLeft: 101, marginTop: 4, padding: "6px 10px", borderRadius: 5, background: "rgba(167,139,250,0.06)", borderLeft: "3px solid #a78bfa", fontSize: 9, color: "var(--text)", lineHeight: 1.5 }}>
                    <span style={{ fontSize: 8, fontWeight: 700, color: "#a78bfa", marginRight: 6 }}>◆ OMEGA ZI AI</span>
                    {li.text}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default LiveScannerFeed;
